
import { PermissionTypeEnum, ProcessTypeEnum, QueryOperator, ShiftModel, SortOrder, TenantUserModel } from '@/libs/Api';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import moment from 'moment-timezone';
import ApiService from '@/libs/core/api-service';
import { shiftStationsStore } from '@/libs/shifts/+state/store';
import ShiftState from '@/libs/shifts/components/ShiftState.vue';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import ProductSelect from '@/libs/common/components-smart/ProductSelect.vue';

// Timezone pro zobrazování a filtrování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

@Component({
    components: { ProductSelect, ShiftTypeSelect, ShiftState },
})
export default class ShiftStations extends Page {
    filterWorkplace: Array<string> | null = null;
    filterStation: Array<string> | null = null;
    filterProduct: Array<string> | null = null;
    filterShiftType: Array<string> | null = null;
    filterChecklistDoneCount: number | null = null;
    filterSince: string | null = null;
    filterTill: string | null = null;
    filterTiming: number | null = null;
    downloading = false;

    mobileFiltersShow = false;
    pageIndex = 1;
    pageSize = 25;
    pagingChanged = false;
    showSecondLine = false;
    debounce: any = null;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected PermissionTypeEnum: typeof PermissionTypeEnum = PermissionTypeEnum;

    get headers() {
        return [
            { text: this.$i18n.t('shifts.shiftDate'), value: 'fromDate', align: 'left', sortable: false },
            { text: this.$i18n.t('common.workplace'), value: 'workplace.code', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.station.name'), value: 'station.code', align: 'left', sortable: false },
            { text: this.$i18n.t('shifts.shiftType'), value: 'shiftType.code', align: 'left', sortable: false },
            { text: this.$i18n.t('audit.plan.shiftTeamType'), value: 'shiftTeamType.code', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.worker'), value: 'station.code', align: 'left', sortable: false },
            {
                text: this.$i18n.t('shifts.checklistDoneCount'),
                value: 'checklistDoneCount',
                align: 'center',
                sortable: false,
            },
        ];
    }

    @Prop() tenantId!: string;

    @Watch('filterPlanItemState')
    filterStateChanged() {
        this.load();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    paginationChanged() {
        this.saveQuery();
        if (this.pagingChanged) {
            this.pagingChanged = false;
            return;
        }
        this.load();
    }

    @Watch('filterWorkplace')
    @Watch('filterStation')
    @Watch('filterShiftType')
    @Watch('filterProduct')
    @Watch('filterSince')
    @Watch('filterTill')
    @Watch('filterTiming')
    @Watch('filterChecklistDoneCount')
    filtersChanged() {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
            this.pageIndex = 1;
            this.load();
        }, 500);
    }

    @Watch('itemsPerPage')
    itemsPerPageChanged(v: number) {
        window.localStorage.amsItemsPerPage = v.toString();
    }

    get loading(): boolean {
        return shiftStationsStore.useGetter(CrudGetter.Pending);
    }

    get shiftStations(): ShiftModel[] {
        return [...shiftStationsStore.useGetter(CrudGetter.Data)];
    }

    get totalRows(): number {
        return ({ ...shiftStationsStore.useGetter(CrudGetter.DataStats) } as CrudDataStats)?.rowCount;
    }

    saveQuery() {
        this.setQuery('pageSize', this.pageSize?.toString());
        window.localStorage.ptPageSize = this.pageSize?.toString();
    }

    mounted() {
        if (this.getQueryInt('pageSize') != null) {
            this.pageSize = this.getQueryInt('pageSize') ?? 25;
        } else {
            if (window.localStorage.ptPageSize && Number(window.localStorage.ptPageSize)) {
                this.pageSize = Number(window.localStorage.ptPageSize);
            }
            this.saveQuery();
        }
        this.load();
    }

    load() {
        shiftStationsStore.dispatch(CrudAction.GetAll, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [];
        let workplaceFilters: CrudQueryPredicate[] = [];
        let stationFilters: CrudQueryPredicate[] = [];
        let shiftTypeFilters: CrudQueryPredicate[] = [];
        let productFilters: CrudQueryPredicate[] = [];
        if (this.filterWorkplace && this.filterWorkplace.length > 0) {
            this.filterWorkplace.forEach((e: string) => {
                if (e) {
                    workplaceFilters.push({
                        field: 'shift.workplaceId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterStation && this.filterStation.length > 0) {
            this.filterStation.forEach((e: string) => {
                if (e) {
                    stationFilters.push({
                        field: 'stationId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterShiftType && this.filterShiftType.length > 0) {
            this.filterShiftType.forEach((e: string) => {
                if (e) {
                    shiftTypeFilters.push({
                        field: 'shift.shiftTypeId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterProduct && this.filterProduct.length > 0) {
            this.filterProduct.forEach((e: string) => {
                if (e) {
                    productFilters.push({
                        field: 'shift.shiftProductions.productId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (workplaceFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: workplaceFilters,
            });
        }
        if (stationFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: stationFilters,
            });
        }
        if (shiftTypeFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: shiftTypeFilters,
            });
        }
        if (productFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: productFilters,
            });
        }
        if (this.filterSince && this.filterSince.length > 0) {
            filters.push({
                field: 'shift.fromDate',
                op: 'gte',
                comparand: moment.tz(this.filterSince, DISPLAY_TIMEZONE).format('YYYY-MM-DDT06:00:00'),
            });
        }
        if (this.filterTill && this.filterTill.length > 0) {
            filters.push({
                field: 'shift.toDate',
                op: 'lte',
                comparand: moment.tz(this.filterTill, DISPLAY_TIMEZONE).add(1, 'day').format('YYYY-MM-DDT06:00:00'),
            });
        }
        if (this.filterTiming == null) {
            filters.push({
                field: 'shift.fromDate',
                op: 'lte',
                comparand: moment.tz(DISPLAY_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss'),
            });
        } else if (this.filterTiming == 1) {
            filters.push({
                field: 'shift.fromDate',
                op: 'gte',
                comparand: moment.tz(DISPLAY_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss'),
            });
        }
        if (
            this.filterChecklistDoneCount != null &&
            !isNaN(Number(this.filterChecklistDoneCount)) &&
            Number(this.filterChecklistDoneCount) >= 0
        ) {
            filters.push({
                field: 'checklistDoneCount',
                op: 'eq',
                comparand: Number(this.filterChecklistDoneCount),
            });
        }
        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<ShiftModel>(
            { field: 'shift.fromDate', index: 1, order: this.filterTiming == null ? 'desc' : 'asc' },
            this.getFilters(),
            {
                pageIndex: this.pageIndex,
                pageSize: this.pageSize,
            }
        );
    }

    exportClick() {
        const filters = this.getFilters();
        this.downloading = true;
        ApiService.api
            .exportShiftStationBatch(1, 999999, {
                predicates: filters.map((x) => {
                    return {
                        field: x.field,
                        op: x.op as QueryOperator,
                        comparand: x.comparand,
                    };
                }),
                orderby: [
                    { field: 'shift.fromDate', index: 1, order: this.filterTiming == null ? SortOrder.Desc : SortOrder.Asc },
                ],
            })
            .then((e) => {
                const res = e.data.returnValue!;
                const blob = b64toBlob(res.base64!, res.contentType!);
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute('download', res.filename!);
                document.body.appendChild(link);
                link.click();
                link.parentNode!.removeChild(link);
                this.downloading = false;
            })
            .catch(() => {
                this.downloading = false;
            });
    }
}
